import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function TermsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/c02c3446a2b3e29ad54a764122882bc0.png" 
                alt="NexBlocX" 
                className="h-40"
              />
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                to="/"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link 
                to="/"
                className="bg-amber-500 text-gray-900 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
              >
                Contact Us
              </Link>
            </div>

            <button 
              className="md:hidden cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-900`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link 
                to="/"
                className="block w-full text-left px-4 py-2 bg-amber-500 text-gray-900 hover:bg-amber-400 rounded-lg font-semibold cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
          <p className="text-sm text-gray-600 mb-12">Last Updated: 01/06/2026</p>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-8">
              Welcome to <strong>NexBlocX</strong>, official distributor of <strong>Polycrete Systems</strong>. These Terms and Conditions ("Terms") govern your access to and use of our website, services, communications, and any related platforms (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, please discontinue use immediately.
            </p>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Use of Services</h2>
              <p className="text-gray-700 mb-4">
                You agree to use the Services only for lawful purposes and in compliance with all applicable laws, regulations, and industry standards. You are responsible for ensuring that any information you provide to NexBlocX is accurate, complete, and current.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Communications and Consent</h2>
              <p className="text-gray-700 mb-4">
                By providing your phone number, email address, or other contact information, you consent to receive communications from <strong>NexBlocX</strong>, including SMS messages, phone calls, and emails related to inquiries, orders, project coordination, account updates, promotions, and customer support.
              </p>
              <p className="text-gray-700 mb-4">
                Message and data rates may apply. Message frequency may vary. You may opt out of SMS communications at any time by replying <strong>STOP</strong>, and you may request assistance by replying <strong>HELP</strong>.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Privacy</h2>
              <p className="text-gray-700 mb-4">
                Your use of the Services is also governed by our Privacy Policy. By using the Services, you acknowledge and agree that <strong>NexBlocX</strong> may collect, store, and process personal and business information in accordance with the Privacy Policy.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                All content, trademarks, logos, designs, and materials available through the Services are the property of NexBlocX, Polycrete Systems, or their respective licensors. You may not reproduce, distribute, modify, or create derivative works without prior written consent.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Estimates and Proposals</h2>
              <p className="text-gray-700 mb-4">
                Any estimates, quotes, or proposals provided by NexBlocX are for informational purposes only and are subject to change based on project specifications, material availability, and site conditions. Final pricing and terms will be confirmed in writing prior to project commencement.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the fullest extent permitted by law, NexBlocX and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Services, even if advised of the possibility of such damages.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Indemnification</h2>
              <p className="text-gray-700 mb-4">
                You agree to indemnify, defend, and hold harmless NexBlocX, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses arising from your use of the Services or violation of these Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Modifications to Terms</h2>
              <p className="text-gray-700 mb-4">
                NexBlocX reserves the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of the Services after any modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the courts located in Florida.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-gray-700">
                <strong>NexBlocX</strong><br />
                Email: info@nexblocx.com<br />
                Phone: 561-918-2055
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-gray-900 text-white py-16 overflow-hidden min-h-[400px]">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/5f47c77c836e004333a2d82b946d5e21.jpeg" 
            alt="Footer Background" 
            className="w-full h-full object-cover object-center absolute inset-0"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4 md:mb-0 text-sm text-gray-400">
                <span>All rights reserved 2026 NexblocX Polycrete Systems</span>
                <span className="hidden md:inline">|</span>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span>|</span>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Powered by Readdy
                </a>
              </div>

              <div className="flex items-center space-x-4">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="X">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
